import ArchiveDocumentTemplate from '@Application/Documents/Templates/Commands/ArchiveDocumentTemplate';
import archiveDocumentTemplate from '@Application/Documents/Templates/archiveDocumentTemplate';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import type DocumentTemplateView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateView';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';
import { Archive } from 'lucide-react';
import * as React from 'react';

type Props = {
	readonly template: DocumentTemplateView;
	readonly organizationId: number;
	/** Affected at the organization, or overridden in at least one facility. The server refuses either
	 * way; this only spares the round trip and names the reason up front. */
	readonly isAssigned: boolean;
};

/**
 * Leaf action, dispatches itself. Archiving is definitive (no un-archiving, ever) and refused
 * server-side while the gabarit is assigned — so a card that is currently assigned shows the blocking
 * message instead of the usual accept/reject pair (`footer` replaces both buttons with a single
 * "Fermer").
 */
const ArchiveTemplateConfirmationDialog: React.FC<Props> = ({ template, organizationId, isAssigned }) => {
	const dispatch = useDispatch();
	const [isVisible, setVisible] = React.useState<boolean>(false);

	const handleOpen = React.useCallback(() => {
		setVisible(true);
	}, []);

	const handleHide = React.useCallback(() => {
		setVisible(false);
	}, []);

	const handleAccept = React.useCallback(() => {
		dispatch(archiveDocumentTemplate(new ArchiveDocumentTemplate(organizationId, template.id))).catch(
			ignoreHandledError,
		);
		setVisible(false);
	}, [dispatch, organizationId, template.id]);

	return (
		<React.Fragment>
			<Button
				type="button"
				severity="danger"
				outlined
				tooltip="Archiver"
				tooltipOptions={{ position: 'top' }}
				icon={<Archive size={16} />}
				onClick={handleOpen}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isVisible}
				onHide={handleHide}
				header="Archiver le gabarit"
				message={
					isAssigned ? (
						<p>Ce gabarit est affecté : retirez d’abord ses affectations.</p>
					) : (
						<p>
							Archiver « {template.name} » ? <br />
							Il ne servira plus à produire de nouveau document. <br />
							Les documents déjà produits le conservent. <br />
							L’archivage est définitif.
						</p>
					)
				}
				accept={isAssigned ? undefined : handleAccept}
				acceptSeverity="danger"
				reject={handleHide}
				footer={isAssigned ? <Button type="button" label="Fermer" onClick={handleHide} /> : undefined}
			/>
		</React.Fragment>
	);
};

export default ArchiveTemplateConfirmationDialog;
